// =====================================================================
//  ADMIN CONSOLE, PAYROLL
// ---------------------------------------------------------------------
//  Payroll is the one screen in this console where a mistake costs money
//  and is hard to take back, so the flow is deliberately not one button.
//
//    Preview   compute the whole register and show it, writing nothing
//    Draft     persist it, still freely recomputable
//    Approve   lock it, the server refuses every further change, and the
//              database refuses it too (migration 0013's triggers)
//    Paid      record the disbursement; amounts stay frozen
//
//  Nothing on this page calculates pay. Every figure, each component,
//  each statutory deduction, the loss-of-pay pro-rating, is computed and
//  persisted server-side. The client shows it and explains it.
//
//  The four tabs follow the order of work: run a period, read the ledger,
//  make sure everyone has a salary on record, configure the rules.
// =====================================================================

import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  AlertTriangle,
  BadgeIndianRupee,
  Ban,
  CalendarClock,
  CheckCircle2,
  Download,
  Eye,
  FileSpreadsheet,
  Info,
  Lock,
  Pencil,
  Play,
  Receipt,
  Settings2,
  ShieldCheck,
  TrendingDown,
  Wallet,
} from 'lucide-react';

import backendApi from '../../api/backendApi';
import DataTable from '../DataTable';
import { useAdminAction } from '../CommandPalette';
import {
  Button,
  ChartTooltip,
  Checkbox,
  Chip,
  ConfirmDialog,
  Drawer,
  EmptyState,
  Field,
  FilterChip,
  Grid,
  Input,
  KeyValue,
  KpiTile,
  LoadingBlock,
  Modal,
  Notice,
  PageHeader,
  Panel,
  PanelBody,
  PanelHead,
  PersonCell,
  SearchInput,
  Select,
  StatusChip,
  Tabs,
  Toolbar,
  ToolbarSpacer,
  toast,
  useDebounced,
} from '../ui';
import { series, toneVar } from '../theme';
import {
  DASH,
  distinct,
  downloadCsv,
  fmtDate,
  fmtDateTime,
  fmtMoney,
  fmtNumber,
  fmtPercent,
  matchesSearch,
  orDash,
  toISODate,
} from '../format';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Run status → the chip tone that reads correctly at a glance. A draft is
// not a warning (nothing is wrong with it), and `paid` is the only state
// that means the money has actually moved.
const RUN_TONE = {
  draft: 'neutral',
  approved: 'info',
  paid: 'positive',
  cancelled: 'negative',
};

const COVERAGE_LABEL = {
  missing: 'No salary on record',
  needs_review: 'Imported, needs confirming',
  no_structure: 'No structure assigned',
  ready: 'Ready',
};

const COVERAGE_TONE = {
  missing: 'negative',
  needs_review: 'warning',
  no_structure: 'warning',
  ready: 'positive',
};

// `toneVar` returns the ABBREVIATED prefix the stylesheet actually uses
// ('neg', not 'negative'), so it has to be interpolated into the variable
// name. Getting this wrong fails silently, an unknown custom property is
// dropped by CSS and the build still passes, which is why these are
// resolved once here instead of inline at each use.
const NEG_FG = `var(--ac-${toneVar('negative')}-fg)`;
const WARN_FG = `var(--ac-${toneVar('warning')}-fg)`;

const num = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Compact read-only table, matching the one the people directory uses in
 * its drawer. Reuses the console's `ac-table` styling rather than
 * inventing a class, so these line up with every other table in the UI.
 */
function MiniTable({ head, aligns = [], children, style }) {
  return (
    <div
      className="ac-tablewrap"
      style={{ border: '1px solid var(--ac-line)', borderRadius: 'var(--ac-r)', ...style }}
    >
      <table className="ac-table">
        {head ? (
          <thead>
            <tr>
              {head.map((heading, i) => (
                <th
                  key={heading || `col-${i}`}
                  className={aligns[i] === 'right' ? 'ac-th--num' : undefined}
                >
                  <span className="ac-th-inner">{heading}</span>
                </th>
              ))}
            </tr>
          </thead>
        ) : null}
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export default function PayrollPage({ can }) {
  const queryClient = useQueryClient();

  // Four separate capabilities, because preparing payroll and authorising
  // it should not be the same act. Migration 0013 also took payroll.run
  // away from managers, who had been granted it when a "run" produced
  // advisory numbers nobody was paid from.
  const canRun = can('payroll.run');
  const canApprove = can('payroll.approve');
  const canConfigure = can('payroll.configure');
  const canAssignSalary = can('salary.assign');

  const [tab, setTab] = useState('runs');

  /* ------------------------------ headline ---------------------------- */
  const { data: snapshot } = useQuery({
    queryKey: ['admin', 'payroll-snapshot'],
    queryFn: () => backendApi.admin.payrollSnapshot(),
  });

  const { data: trend = [] } = useQuery({
    queryKey: ['admin', 'payroll-trend', 12],
    queryFn: () => backendApi.admin.payrollTrend(12),
  });

  const { data: coverage } = useQuery({
    queryKey: ['admin', 'payroll-coverage'],
    queryFn: () => backendApi.payroll.salaryCoverage(),
  });

  const unready = coverage
    ? num(coverage.counts.missing) + num(coverage.counts.needs_review) + num(coverage.counts.no_structure)
    : 0;

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin'] });

  useAdminAction('payroll:run', () => {
    if (canRun) setTab('runs');
  });

  const tabs = [
    { key: 'runs', label: 'Runs', icon: Play },
    { key: 'register', label: 'Register', icon: Receipt },
    { key: 'salaries', label: 'Salaries', icon: BadgeIndianRupee, count: unready || null },
    { key: 'setup', label: 'Setup', icon: Settings2 },
  ];

  return (
    <div className="ac-page ac-page--wide">
      <PageHeader
        title="Payroll"
        sub="Each payslip is computed from the employee's salary structure, the period's attendance and the organization's statutory settings."
      />

      {snapshot ? (
        <Grid cols={4} style={{ marginBottom: 14 }}>
          <KpiTile
            label={`Net · ${snapshot.month} ${snapshot.year}`}
            value={fmtMoney(snapshot.netCurrent, { compact: true })}
            icon={Wallet}
            delta={snapshot.netDelta}
            spark={trend.map((m) => m.net)}
            foot={
              snapshot.runStatus === 'draft'
                ? `Draft · ${fmtMoney(snapshot.netDraft, { compact: true })} pending approval`
                : snapshot.payslipsCurrent
                  ? `${snapshot.payslipsCurrent} payslips approved`
                  : 'Not run yet'
            }
          />
          <KpiTile
            label="Gross this month"
            value={fmtMoney(snapshot.grossCurrent, { compact: true })}
            icon={BadgeIndianRupee}
            foot="Before deductions"
          />
          <KpiTile
            label="Deductions"
            value={fmtMoney(snapshot.deductionsCurrent, { compact: true })}
            icon={TrendingDown}
            foot={
              snapshot.grossCurrent > 0
                ? `${fmtPercent((snapshot.deductionsCurrent / snapshot.grossCurrent) * 100)} of gross`
                : 'No approved run yet'
            }
          />
          <KpiTile
            label="Payslips issued"
            value={fmtNumber(snapshot.payslipsAllTime)}
            icon={Receipt}
            foot="All time"
          />
        </Grid>
      ) : null}

      {/* The single most useful warning on this page: payroll cannot cover
          somebody who has no salary on record, and the first run is the
          wrong moment to find that out. */}
      {unready > 0 ? (
        <div style={{ marginBottom: 14 }}>
          <Notice
            tone="warning"
            title={`${unready} employee${unready === 1 ? '' : 's'} cannot be paid yet`}
            actions={
              <Button size="sm" onClick={() => setTab('salaries')}>
                Review salaries
              </Button>
            }
          >
            {num(coverage.counts.missing) > 0
              ? `${coverage.counts.missing} have no salary on record. `
              : ''}
            {num(coverage.counts.needs_review) > 0
              ? `${coverage.counts.needs_review} were imported from the old salary field and have not been confirmed. `
              : ''}
            {num(coverage.counts.no_structure) > 0
              ? `${coverage.counts.no_structure} have no salary structure. `
              : ''}
            A payroll run skips them and says so, rather than guessing a figure.
          </Notice>
        </div>
      ) : null}

      <div style={{ marginBottom: 14 }}>
        <Tabs items={tabs} value={tab} onChange={setTab} />
      </div>

      {tab === 'runs' ? (
        <RunsTab
          canRun={canRun}
          canApprove={canApprove}
          trend={trend}
          onDone={invalidate}
        />
      ) : null}

      {tab === 'register' ? <RegisterTab /> : null}

      {tab === 'salaries' ? (
        <SalariesTab coverage={coverage} canAssign={canAssignSalary} onDone={invalidate} />
      ) : null}

      {tab === 'setup' ? <SetupTab canConfigure={canConfigure} /> : null}
    </div>
  );
}

/* =====================================================================
   RUNS, prepare, preview, approve, pay
   ================================================================== */

function RunsTab({ canRun, canApprove, trend, onDone }) {
  const queryClient = useQueryClient();
  const now = new Date();

  const [month, setMonth] = useState(MONTHS[now.getMonth()]);
  const [year, setYear] = useState(now.getFullYear());
  const [previewOpen, setPreviewOpen] = useState(false);
  const [openRunId, setOpenRunId] = useState(null);
  const [pendingCancel, setPendingCancel] = useState(null);
  const [pendingApprove, setPendingApprove] = useState(null);

  const { data: runs = [], isLoading } = useQuery({
    queryKey: ['admin', 'payroll-runs'],
    queryFn: () => backendApi.payroll.runs({ limit: 36 }),
  });

  // The server computes this; the client only asks. Deliberately not
  // `enabled: true`, a preview walks every employee's attendance for the
  // period, so it runs when an administrator asks for it.
  const {
    data: preview,
    isFetching: previewing,
    refetch: refetchPreview,
  } = useQuery({
    queryKey: ['admin', 'payroll-preview', month, year],
    queryFn: () => backendApi.payroll.preview({ month, year }),
    enabled: previewOpen,
  });

  const existing = runs.find(
    (r) => r.month === month && Number(r.year) === Number(year) && r.status !== 'cancelled'
  );

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['admin'] });
    onDone?.();
  };

  const draft = useMutation({
    mutationFn: () => backendApi.payroll.run({ month, year }),
    onSuccess: (result) => {
      toast.success(
        `Draft ready for ${result.month} ${result.year}, ${result.processed} payslip(s)` +
          (result.skipped?.length ? `, ${result.skipped.length} skipped` : '')
      );
      setPreviewOpen(false);
      setOpenRunId(result.runId);
      refresh();
    },
    onError: (err) => toast.error(err.message || 'Could not prepare the payroll run'),
  });

  const approve = useMutation({
    mutationFn: (id) => backendApi.payroll.approve(id),
    onSuccess: (run) => {
      toast.success(`${run.month} ${run.year} approved. The figures are now locked.`);
      setPendingApprove(null);
      refresh();
    },
    onError: (err) => toast.error(err.message || 'Could not approve this run'),
  });

  const pay = useMutation({
    mutationFn: (id) => backendApi.payroll.markPaid(id, { payDate: toISODate() }),
    onSuccess: (run) => {
      toast.success(`${run.month} ${run.year} marked paid.`);
      refresh();
    },
    onError: (err) => toast.error(err.message || 'Could not mark this run paid'),
  });

  const cancel = useMutation({
    mutationFn: (id) => backendApi.payroll.cancel(id, { reason: 'Cancelled from the console' }),
    onSuccess: () => {
      toast.success('Run cancelled. The period can be prepared again.');
      setPendingCancel(null);
      refresh();
    },
    onError: (err) => toast.error(err.message || 'Could not cancel this run'),
  });

  const runColumns = useMemo(
    () => [
      {
        key: 'period',
        label: 'Period',
        width: 120,
        render: (row) => (
          <strong style={{ color: 'var(--ac-ink)' }}>
            {row.month} {row.year}
          </strong>
        ),
        sortValue: (row) => row.period_start,
      },
      {
        key: 'status',
        label: 'Status',
        width: 110,
        render: (row) => (
          <Chip tone={RUN_TONE[row.status] || 'neutral'} dot>
            {row.status}
          </Chip>
        ),
      },
      {
        key: 'employee_count',
        label: 'Payslips',
        width: 90,
        align: 'right',
        render: (row) => (
          <span className="ac-num">
            {fmtNumber(row.employee_count)}
            {num(row.skipped_count) > 0 ? (
              <span style={{ color: WARN_FG, marginLeft: 5 }}>
                +{row.skipped_count} skipped
              </span>
            ) : null}
          </span>
        ),
      },
      {
        key: 'total_gross',
        label: 'Gross',
        width: 115,
        align: 'right',
        render: (row) => fmtMoney(row.total_gross),
      },
      {
        key: 'total_net',
        label: 'Net payout',
        width: 125,
        align: 'right',
        render: (row) => <strong style={{ color: 'var(--ac-ink)' }}>{fmtMoney(row.total_net)}</strong>,
      },
      {
        key: 'total_employer_cost',
        label: 'Employer cost',
        width: 125,
        align: 'right',
        render: (row) => fmtMoney(row.total_employer_cost),
      },
      {
        key: 'approved_by_name',
        label: 'Approved by',
        width: 150,
        render: (row) =>
          row.approved_at ? (
            <PersonCell name={row.approved_by_name || 'Unknown'} sub={fmtDate(row.approved_at)} />
          ) : (
            DASH
          ),
      },
    ],
    []
  );

  return (
    <>
      <Panel style={{ marginBottom: 14 }}>
        <PanelHead
          title="Prepare a period"
          sub="Preview first: the register is computed in full without anything being written."
        />
        <PanelBody>
          <Grid cols={4}>
            <Field label="Month" required>
              <Select value={month} onChange={(e) => setMonth(e.target.value)} options={MONTHS} />
            </Field>
            <Field label="Year" required>
              <Select
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                options={[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => ({
                  value: y,
                  label: String(y),
                }))}
              />
            </Field>
            <Field label="Current state" hint={existing ? undefined : 'Nothing prepared yet'}>
              <div style={{ paddingTop: 7 }}>
                {existing ? (
                  <Chip tone={RUN_TONE[existing.status] || 'neutral'} dot>
                    {existing.status}
                  </Chip>
                ) : (
                  <Chip>Not started</Chip>
                )}
              </div>
            </Field>
            <Field label="&nbsp;">
              <Button
                icon={Eye}
                onClick={() => {
                  setPreviewOpen(true);
                  refetchPreview();
                }}
                disabled={!canRun}
              >
                Preview {month} {year}
              </Button>
            </Field>
          </Grid>

          {existing && existing.status !== 'draft' ? (
            <div style={{ marginTop: 12 }}>
              <Notice
                tone={existing.status === 'cancelled' ? 'neutral' : 'info'}
                icon={Lock}
                title={`${month} ${year} is ${existing.status}`}
              >
                An approved period cannot be re-run or edited, the figures are final. To redo it, cancel
                the run first; a cancelled run stays on the record rather than disappearing.
              </Notice>
            </div>
          ) : null}
        </PanelBody>
      </Panel>

      <Panel style={{ marginBottom: 14 }}>
        <PanelHead title="Cost trend" sub="Net payout and deductions per month, cancelled runs excluded" />
        <PanelBody>
          {trend.every((m) => m.gross === 0) ? (
            <EmptyState
              icon={Wallet}
              title="No payroll history"
              text="Prepare and approve a period to start building the trend."
            />
          ) : (
            <ResponsiveContainer width="100%" height={230}>
              <BarChart data={trend} margin={{ top: 6, right: 8, left: 0, bottom: 0 }} barCategoryGap="24%">
                <CartesianGrid strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="label" tickLine={false} axisLine={false} />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  width={54}
                  tickFormatter={(v) => fmtMoney(v, { compact: true })}
                />
                <Tooltip
                  content={<ChartTooltip formatter={(v) => fmtMoney(v)} />}
                  cursor={{ fill: 'rgba(15,20,25,0.04)' }}
                />
                <Legend
                  iconType="square"
                  iconSize={8}
                  wrapperStyle={{ fontSize: 11, paddingTop: 6, color: 'var(--ac-muted)' }}
                />
                <Bar dataKey="net" name="Net" stackId="a" fill={series.accent} />
                <Bar dataKey="deductions" name="Deductions" stackId="a" fill={series.slate} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </PanelBody>
      </Panel>

      <DataTable
        columns={runColumns}
        rows={runs}
        loading={isLoading}
        onRowClick={(row) => setOpenRunId(row.id)}
        defaultSort={{ key: 'period', dir: 'desc' }}
        emptyIcon={CalendarClock}
        emptyTitle="No payroll runs"
        emptyText="Preview a period above, then save it as a draft."
        toolbar={
          <Toolbar>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ac-ink)' }}>Run history</span>
            <ToolbarSpacer />
            <span style={{ fontSize: 11.5, color: 'var(--ac-muted)' }}>
              {fmtNumber(runs.length)} run{runs.length === 1 ? '' : 's'}
            </span>
          </Toolbar>
        }
      />

      {/* ------------------------- PREVIEW ------------------------- */}
      <Drawer
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title={`Preview · ${month} ${year}`}
        sub="Computed from live salaries, attendance and statutory settings. Nothing has been saved."
        wide
        footer={
          <>
            <Button onClick={() => setPreviewOpen(false)} disabled={draft.isPending}>
              Close
            </Button>
            <div style={{ flex: '1 1 auto' }} />
            <Button
              variant="primary"
              icon={Play}
              onClick={() => draft.mutate()}
              loading={draft.isPending}
              disabled={!canRun || !preview?.rows?.length || existing?.status === 'approved' || existing?.status === 'paid'}
            >
              {existing?.status === 'draft'
                ? 'Recompute the draft'
                : `Save as draft · ${preview?.rows?.length || 0} payslips`}
            </Button>
          </>
        }
      >
        {previewing ? (
          <LoadingBlock label="Computing the register…" />
        ) : !preview ? (
          <EmptyState icon={Info} title="Nothing to preview" />
        ) : (
          <PreviewBody preview={preview} existing={existing} />
        )}
      </Drawer>

      {/* -------------------------- ONE RUN -------------------------- */}
      <RunDrawer
        runId={openRunId}
        onClose={() => setOpenRunId(null)}
        canApprove={canApprove}
        canRun={canRun}
        onApprove={(run) => setPendingApprove(run)}
        onPay={(run) => pay.mutate(run.id)}
        onCancel={(run) => setPendingCancel(run)}
        paying={pay.isPending}
      />

      <ConfirmDialog
        open={Boolean(pendingApprove)}
        onClose={() => setPendingApprove(null)}
        onConfirm={() => approve.mutate(pendingApprove.id)}
        loading={approve.isPending}
        title="Approve this payroll run?"
        confirmLabel="Approve and lock"
        message={
          <>
            Approving <strong>{pendingApprove?.month} {pendingApprove?.year}</strong> locks every figure in
            it. After this the amounts cannot be edited, the payslips cannot be deleted and the period
            cannot be re-run, the database enforces that, not just this screen. Employees will be able to
            see their payslips.
            <div style={{ marginTop: 8 }}>
              Net payout: <strong>{fmtMoney(pendingApprove?.total_net)}</strong> across{' '}
              {fmtNumber(pendingApprove?.employee_count)} payslip(s).
            </div>
          </>
        }
      />

      <ConfirmDialog
        open={Boolean(pendingCancel)}
        onClose={() => setPendingCancel(null)}
        onConfirm={() => cancel.mutate(pendingCancel.id)}
        loading={cancel.isPending}
        title="Cancel this payroll run?"
        confirmLabel="Cancel the run"
        message={
          <>
            <strong>{pendingCancel?.month} {pendingCancel?.year}</strong> will be marked cancelled and the
            period can be prepared again. The cancelled attempt stays on the record, along with who made
            it, it is not deleted.
          </>
        }
      />
    </>
  );
}

/** The preview drawer's body: totals, exclusions, then the register. */
function PreviewBody({ preview, existing }) {
  const totals = preview.totals || {};
  const needsReview = (preview.rows || []).filter((r) => r.needs_salary_review);

  return (
    <>
      <KeyValue
        items={[
          { key: 'Payslips', value: <strong className="ac-num">{fmtNumber(preview.rows.length)}</strong> },
          { key: 'Gross earnings', value: <span className="ac-num">{fmtMoney(totals.gross)}</span> },
          {
            key: 'Employee deductions',
            value: (
              <span className="ac-num" style={{ color: NEG_FG }}>
                − {fmtMoney(totals.deductions)}
              </span>
            ),
          },
          {
            key: 'Net payout',
            value: (
              <strong className="ac-num" style={{ fontSize: 15 }}>
                {fmtMoney(totals.net)}
              </strong>
            ),
          },
          {
            key: 'Total employer cost',
            value: <span className="ac-num">{fmtMoney(totals.employerCost)}</span>,
            // Gross plus the employer's PF/ESI share. Not deducted from
            // anybody, it is what the month costs the company.
          },
        ]}
      />

      {existing?.status === 'draft' ? (
        <div style={{ marginTop: 14 }}>
          <Notice tone="info" icon={Info} title="A draft already exists for this period">
            Saving replaces the draft's payslips with these figures. That is safe, a draft is not a
            payslip anyone has seen, and employees cannot read it until it is approved.
          </Notice>
        </div>
      ) : null}

      {preview.skipped?.length ? (
        <div style={{ marginTop: 14 }}>
          <Notice
            tone="negative"
            icon={AlertTriangle}
            title={`${preview.skipped.length} employee(s) will be left out`}
          >
            <div style={{ display: 'grid', gap: 4, marginTop: 4 }}>
              {preview.skipped.map((s) => (
                <div key={s.employeeId} style={{ fontSize: 12 }}>
                  <strong>{s.name}</strong>
                  {s.code ? <span style={{ color: 'var(--ac-muted)' }}> · {s.code}</span> : null}, {s.reason}
                </div>
              ))}
            </div>
          </Notice>
        </div>
      ) : null}

      {needsReview.length ? (
        <div style={{ marginTop: 14 }}>
          <Notice
            tone="warning"
            icon={AlertTriangle}
            title={`${needsReview.length} salary figure(s) have not been confirmed`}
          >
            These were imported from the old single salary column, whose unit was never recorded, so the
            annual figure is a reading of it rather than a stated fact. Confirm them under Salaries
            before approving a run.
          </Notice>
        </div>
      ) : null}

      <div className="ac-eyebrow" style={{ margin: '20px 0 7px' }}>
        Register
      </div>
      <MiniTable
        head={['Employee', 'Paid / payable', 'LOP', 'Gross', 'Deductions', 'Net']}
        aligns={['left', 'right', 'right', 'right', 'right', 'right']}
      >
        {preview.rows.map((row) => (
          <tr key={row.employee_id}>
            <td>
              {row.employee_name}
              {row.needs_salary_review ? (
                <Chip tone="warning" style={{ marginLeft: 6 }}>
                  unconfirmed
                </Chip>
              ) : null}
            </td>
            <td className="ac-td--num">
              {fmtNumber(row.paid_days)} / {fmtNumber(row.payable_days)}
            </td>
            <td className="ac-td--num">
              {num(row.lop_days) > 0 ? (
                <span style={{ color: NEG_FG }}>{fmtNumber(row.lop_days)}</span>
              ) : (
                DASH
              )}
            </td>
            <td className="ac-td--num">{fmtMoney(row.gross_salary)}</td>
            <td className="ac-td--num">
              {num(row.deductions) > 0 ? `− ${fmtMoney(row.deductions)}` : DASH}
            </td>
            <td className="ac-td--num">
              <strong>{fmtMoney(row.net_salary)}</strong>
            </td>
          </tr>
        ))}
      </MiniTable>
    </>
  );
}

/** One run: its totals, its lifecycle actions, and its payslips. */
function RunDrawer({ runId, onClose, canApprove, canRun, onApprove, onPay, onCancel, paying }) {
  const { data: run, isLoading } = useQuery({
    queryKey: ['admin', 'payroll-run', runId],
    queryFn: () => backendApi.payroll.getRun(runId),
    enabled: Boolean(runId),
  });

  const [slipId, setSlipId] = useState(null);

  return (
    <>
      <Drawer
        open={Boolean(runId)}
        onClose={onClose}
        title={run ? `Payroll · ${run.month} ${run.year}` : 'Payroll run'}
        sub={run ? `${fmtDate(run.period_start)}, ${fmtDate(run.period_end)}` : null}
        badge={
          run ? (
            <Chip tone={RUN_TONE[run.status] || 'neutral'} dot>
              {run.status}
            </Chip>
          ) : null
        }
        wide
        footer={
          <>
            <Button onClick={onClose}>Close</Button>
            <div style={{ flex: '1 1 auto' }} />
            {run && run.status === 'draft' && canRun ? (
              <Button variant="danger" icon={Ban} onClick={() => onCancel(run)}>
                Cancel run
              </Button>
            ) : null}
            {run && run.status === 'draft' && canApprove ? (
              <Button variant="primary" icon={ShieldCheck} onClick={() => onApprove(run)}>
                Approve and lock
              </Button>
            ) : null}
            {run && run.status === 'approved' && canApprove ? (
              <Button variant="primary" icon={CheckCircle2} onClick={() => onPay(run)} loading={paying}>
                Mark paid
              </Button>
            ) : null}
          </>
        }
      >
        {isLoading ? (
          <LoadingBlock label="Loading the run…" />
        ) : !run ? (
          <EmptyState icon={Info} title="Run not found" />
        ) : (
          <>
            {run.status === 'draft' ? (
              <div style={{ marginBottom: 14 }}>
                <Notice tone="neutral" icon={Pencil} title="This is a draft">
                  Employees cannot see these payslips yet. Recompute it as often as you need; approving is
                  what makes it final and visible.
                </Notice>
              </div>
            ) : null}

            {run.status === 'approved' || run.status === 'paid' ? (
              <div style={{ marginBottom: 14 }}>
                <Notice tone="positive" icon={Lock} title={`Locked on ${fmtDateTime(run.approved_at)}`}>
                  Approved by {run.approved_by_name || 'an administrator'}. The amounts in this run can no
                  longer be changed.
                  {run.paid_at ? ` Marked paid on ${fmtDate(run.pay_date || run.paid_at)}.` : ''}
                </Notice>
              </div>
            ) : null}

            <KeyValue
              items={[
                { key: 'Payslips', value: <strong className="ac-num">{fmtNumber(run.employee_count)}</strong> },
                { key: 'Gross earnings', value: <span className="ac-num">{fmtMoney(run.total_gross)}</span> },
                {
                  key: 'Employee deductions',
                  value: (
                    <span className="ac-num" style={{ color: NEG_FG }}>
                      − {fmtMoney(run.total_deductions)}
                    </span>
                  ),
                },
                {
                  key: 'Net payout',
                  value: (
                    <strong className="ac-num" style={{ fontSize: 15 }}>
                      {fmtMoney(run.total_net)}
                    </strong>
                  ),
                },
                { key: 'Total employer cost', value: <span className="ac-num">{fmtMoney(run.total_employer_cost)}</span> },
                { key: 'Prepared by', value: run.created_by_name },
                { key: 'Prepared', value: fmtDateTime(run.created_at) },
                { key: 'Pay date', value: run.pay_date ? fmtDate(run.pay_date) : null },
              ]}
            />

            {run.skipped?.length ? (
              <div style={{ marginTop: 14 }}>
                <Notice
                  tone="warning"
                  icon={AlertTriangle}
                  title={`${run.skipped.length} employee(s) were left out of this run`}
                >
                  <div style={{ display: 'grid', gap: 4, marginTop: 4 }}>
                    {run.skipped.map((s) => (
                      <div key={s.employeeId} style={{ fontSize: 12 }}>
                        <strong>{s.name}</strong>, {s.reason}
                      </div>
                    ))}
                  </div>
                </Notice>
              </div>
            ) : null}

            <div className="ac-eyebrow" style={{ margin: '20px 0 7px' }}>
              Payslips
            </div>
            <MiniTable
              head={['Employee', 'Paid days', 'LOP', 'Gross', 'Net', '']}
              aligns={['left', 'right', 'right', 'right', 'right', 'right']}
            >
              {run.payslips.map((slip) => (
                <tr key={slip.id}>
                  <td>{slip.employee_name}</td>
                  <td className="ac-td--num">
                    {fmtNumber(slip.paid_days)} / {fmtNumber(slip.payable_days)}
                  </td>
                  <td className="ac-td--num">
                    {num(slip.lop_days) > 0 ? fmtNumber(slip.lop_days) : DASH}
                  </td>
                  <td className="ac-td--num">{fmtMoney(slip.gross_salary)}</td>
                  <td className="ac-td--num">
                    <strong>{fmtMoney(slip.net_salary)}</strong>
                  </td>
                  <td className="ac-td--num">
                    <Button size="sm" variant="ghost" onClick={() => setSlipId(slip.id)}>
                      Payslip
                    </Button>
                  </td>
                </tr>
              ))}
            </MiniTable>
          </>
        )}
      </Drawer>

      <PayslipDrawer payslipId={slipId} onClose={() => setSlipId(null)} />
    </>
  );
}

/* =====================================================================
   REGISTER, the ledger of every payslip
   ================================================================== */

function RegisterTab() {
  const [search, setSearch] = useState('');
  const [periodFilter, setPeriodFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [slipId, setSlipId] = useState(null);

  const debouncedSearch = useDebounced(search, 250);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'payroll-register'],
    queryFn: () =>
      backendApi.entities.Payroll.page({ limit: 1000, sort: 'created_at', order: 'desc' }),
  });

  const register = useMemo(() => data?.rows || [], [data]);

  const periods = useMemo(
    () => distinct(register.map((r) => ({ period: `${r.month} ${r.year}` })), 'period'),
    [register]
  );

  const filtered = useMemo(
    () =>
      register.filter(
        (row) =>
          matchesSearch(row, debouncedSearch, ['employee_name', 'employee_code', 'department', 'position']) &&
          (!periodFilter || `${row.month} ${row.year}` === periodFilter) &&
          (!statusFilter || String(row.status).toLowerCase() === statusFilter.toLowerCase())
      ),
    [register, debouncedSearch, periodFilter, statusFilter]
  );

  const filteredTotals = useMemo(
    () =>
      filtered.reduce(
        (acc, row) => ({
          gross: acc.gross + num(row.gross_salary),
          deductions: acc.deductions + num(row.deductions),
          net: acc.net + num(row.net_salary),
        }),
        { gross: 0, deductions: 0, net: 0 }
      ),
    [filtered]
  );

  const columns = useMemo(
    () => [
      {
        key: 'employee_name',
        label: 'Employee',
        width: 215,
        render: (row) => (
          <PersonCell name={row.employee_name} sub={row.employee_code || row.position || row.department} />
        ),
      },
      { key: 'department', label: 'Department', width: 140, render: (row) => orDash(row.department) },
      {
        key: 'month',
        label: 'Period',
        width: 105,
        render: (row) => (
          <span className="ac-num">
            {row.month} {row.year}
          </span>
        ),
        csv: (row) => `${row.month} ${row.year}`,
        // Sorts on the real date rather than the month name. The register
        // holds both 'Jul' and 'July' from before the engine existed, so
        // an alphabetical sort on the name was wrong twice over.
        sortValue: (row) =>
          row.period_start || `${row.year}-${String(MONTHS.indexOf(row.month) + 1).padStart(2, '0')}-01`,
      },
      {
        key: 'paid_days',
        label: 'Paid days',
        width: 100,
        align: 'right',
        render: (row) =>
          row.payable_days == null ? (
            DASH
          ) : (
            <span className="ac-num">
              {fmtNumber(row.paid_days)} / {fmtNumber(row.payable_days)}
            </span>
          ),
        csv: (row) => row.paid_days,
      },
      {
        key: 'lop_days',
        label: 'LOP',
        width: 70,
        align: 'right',
        render: (row) =>
          num(row.lop_days) > 0 ? (
            <span className="ac-num" style={{ color: NEG_FG }}>
              {fmtNumber(row.lop_days)}
            </span>
          ) : (
            DASH
          ),
        csv: (row) => row.lop_days,
      },
      {
        key: 'gross_salary',
        label: 'Gross',
        width: 112,
        align: 'right',
        render: (row) => fmtMoney(row.gross_salary),
        csv: (row) => row.gross_salary,
      },
      {
        key: 'deductions',
        label: 'Deductions',
        width: 112,
        align: 'right',
        render: (row) => (
          <span style={{ color: NEG_FG }}>
            {num(row.deductions) > 0 ? `− ${fmtMoney(row.deductions)}` : DASH}
          </span>
        ),
        csv: (row) => row.deductions,
      },
      {
        key: 'net_salary',
        label: 'Net pay',
        width: 118,
        align: 'right',
        render: (row) => <strong style={{ color: 'var(--ac-ink)' }}>{fmtMoney(row.net_salary)}</strong>,
        csv: (row) => row.net_salary,
      },
      {
        key: 'status',
        label: 'Status',
        width: 105,
        render: (row) => <StatusChip value={row.status} />,
      },
      { key: 'pay_date', label: 'Pay date', width: 110, render: (row) => fmtDate(row.pay_date) },
    ],
    []
  );

  const exportCsv = () => {
    downloadCsv(`payroll-${periodFilter || 'all'}-${toISODate()}`, filtered, [
      { key: 'employee_code', label: 'Employee ID' },
      { key: 'employee_name', label: 'Employee' },
      { key: 'department', label: 'Department' },
      { key: 'position', label: 'Position' },
      { key: 'month', label: 'Month' },
      { key: 'year', label: 'Year' },
      { key: 'payable_days', label: 'Payable days' },
      { key: 'paid_days', label: 'Paid days' },
      { key: 'lop_days', label: 'Loss of pay days' },
      { key: 'gross_salary', label: 'Gross' },
      { key: 'deductions', label: 'Deductions' },
      { key: 'net_salary', label: 'Net' },
      { key: 'employer_cost', label: 'Employer cost' },
      { key: 'status', label: 'Status' },
      { key: 'pay_date', label: 'Pay date' },
    ]);
    toast.success(`Exported ${filtered.length} payslips`);
  };

  return (
    <>
      <DataTable
        columns={columns}
        rows={filtered}
        loading={isLoading}
        onRowClick={(row) => setSlipId(row.id)}
        defaultSort={{ key: 'month', dir: 'desc' }}
        emptyIcon={Receipt}
        emptyTitle={register.length === 0 ? 'No payroll history' : 'No matching payslips'}
        emptyText={
          register.length === 0
            ? 'Prepare a period under Runs to generate payslips.'
            : 'Adjust the search or filters above.'
        }
        toolbar={
          <Toolbar>
            <SearchInput value={search} onChange={setSearch} placeholder="Search employee…" width={210} />
            <FilterChip label="Period" value={periodFilter} options={periods} onChange={setPeriodFilter} />
            <FilterChip
              label="Status"
              value={statusFilter}
              options={['Draft', 'Processed', 'Paid', 'Pending', 'Cancelled']}
              onChange={setStatusFilter}
            />
            {search || periodFilter || statusFilter ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setSearch('');
                  setPeriodFilter('');
                  setStatusFilter('');
                }}
              >
                Clear
              </Button>
            ) : null}
            <ToolbarSpacer />
            <Button size="sm" icon={Download} onClick={exportCsv} disabled={filtered.length === 0}>
              Export
            </Button>
            <span style={{ fontSize: 11.5, color: 'var(--ac-muted)' }}>
              Net{' '}
              <strong className="ac-num" style={{ color: 'var(--ac-ink)' }}>
                {fmtMoney(filteredTotals.net)}
              </strong>
              {' · '}
              {fmtNumber(filtered.length)} payslips
            </span>
          </Toolbar>
        }
      />

      <PayslipDrawer payslipId={slipId} onClose={() => setSlipId(null)} />
    </>
  );
}

/* =====================================================================
   PAYSLIP, the breakdown, line by line
   ================================================================== */

function PayslipDrawer({ payslipId, onClose }) {
  const { data: slip, isLoading } = useQuery({
    queryKey: ['admin', 'payslip', payslipId],
    queryFn: () => backendApi.payroll.payslip(payslipId),
    enabled: Boolean(payslipId),
  });

  const lineRows = (lines, { hideZero = false } = {}) =>
    (lines || []).filter((l) => !hideZero || num(l.amount) !== 0 || num(l.full_amount) !== 0);

  return (
    <Drawer
      open={Boolean(payslipId)}
      onClose={onClose}
      title={slip?.employee_name || 'Payslip'}
      sub={slip ? `${slip.month} ${slip.year}` : null}
      badge={slip ? <StatusChip value={slip.status} /> : null}
      footer={<Button onClick={onClose}>Close</Button>}
    >
      {isLoading ? (
        <LoadingBlock label="Loading the payslip…" />
      ) : !slip ? (
        <EmptyState icon={Info} title="Payslip not found" />
      ) : (
        <>
          {slip.is_legacy ? (
            <div style={{ marginBottom: 14 }}>
              <Notice tone="neutral" icon={Info} title="Historical payslip">
                This row predates the payroll engine. It was produced by the previous flat-rate
                calculation, so there is no component breakdown to show, only the totals that were
                recorded at the time.
              </Notice>
            </div>
          ) : null}

          <div className="ac-eyebrow" style={{ marginBottom: 7 }}>
            Employee
          </div>
          <KeyValue
            items={[
              { key: 'Name', value: slip.employee_name },
              { key: 'Employee ID', value: slip.employee_code },
              { key: 'Department', value: slip.department },
              { key: 'Position', value: slip.position },
            ]}
          />

          {slip.payable_days != null ? (
            <>
              <div className="ac-eyebrow" style={{ margin: '20px 0 7px' }}>
                Days
              </div>
              <KeyValue
                items={[
                  {
                    key: 'Paid / payable',
                    value: (
                      <span className="ac-num">
                        {fmtNumber(slip.paid_days)} / {fmtNumber(slip.payable_days)}
                      </span>
                    ),
                  },
                  {
                    key: 'Loss of pay',
                    value:
                      num(slip.lop_days) > 0 ? (
                        <span className="ac-num" style={{ color: NEG_FG }}>
                          {fmtNumber(slip.lop_days)} day(s)
                        </span>
                      ) : null,
                  },
                  { key: 'Present', value: <span className="ac-num">{fmtNumber(slip.present_days)}</span> },
                  {
                    key: 'Paid leave',
                    value: num(slip.paid_leave_days) > 0 ? fmtNumber(slip.paid_leave_days) : null,
                  },
                  {
                    key: 'Unpaid leave',
                    value: num(slip.unpaid_leave_days) > 0 ? fmtNumber(slip.unpaid_leave_days) : null,
                  },
                  { key: 'Week offs', value: fmtNumber(slip.week_off_days) },
                  {
                    key: 'Holidays',
                    value: num(slip.holiday_days) > 0 ? fmtNumber(slip.holiday_days) : null,
                  },
                ]}
              />
            </>
          ) : null}

          {/* The whole reason payslip_lines exists: a payslip that cannot
              explain its own numbers is just three figures. */}
          {slip.earning_lines?.length ? (
            <>
              <div className="ac-eyebrow" style={{ margin: '20px 0 7px' }}>
                Earnings
              </div>
              <LineTable rows={slip.earning_lines} />
            </>
          ) : null}

          {lineRows(slip.deduction_lines, { hideZero: true }).length ? (
            <>
              <div className="ac-eyebrow" style={{ margin: '20px 0 7px' }}>
                Deductions
              </div>
              <LineTable rows={lineRows(slip.deduction_lines, { hideZero: true })} negative />
            </>
          ) : null}

          <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--ac-line)' }}>
            <KeyValue
              items={[
                { key: 'Gross earnings', value: <span className="ac-num">{fmtMoney(slip.gross_salary)}</span> },
                {
                  key: 'Total deductions',
                  value: (
                    <span className="ac-num" style={{ color: NEG_FG }}>
                      − {fmtMoney(slip.deductions)}
                    </span>
                  ),
                },
                {
                  key: 'Net pay',
                  value: (
                    <strong className="ac-num" style={{ fontSize: 16 }}>
                      {fmtMoney(slip.net_salary)}
                    </strong>
                  ),
                },
              ]}
            />
          </div>

          {lineRows(slip.employer_lines, { hideZero: true }).length ? (
            <>
              <div className="ac-eyebrow" style={{ margin: '20px 0 7px' }}>
                Employer contributions
              </div>
              <LineTable rows={lineRows(slip.employer_lines, { hideZero: true })} />
              <div style={{ fontSize: 11.5, color: 'var(--ac-muted)', marginTop: 6 }}>
                Paid by the organization on top of the gross, not deducted from net pay. Total cost of
                this month: <strong>{fmtMoney(slip.employer_cost)}</strong>.
              </div>
            </>
          ) : null}

          <div className="ac-eyebrow" style={{ margin: '20px 0 7px' }}>
            Record
          </div>
          <KeyValue
            items={[
              { key: 'Period', value: `${slip.month} ${slip.year}` },
              { key: 'Annual gross on record', value: slip.annual_gross ? fmtMoney(slip.annual_gross) : null },
              { key: 'Run status', value: slip.run_status ? <Chip tone={RUN_TONE[slip.run_status]}>{slip.run_status}</Chip> : null },
              { key: 'Locked', value: slip.approved_at ? fmtDateTime(slip.approved_at) : null },
              { key: 'Pay date', value: fmtDate(slip.pay_date) },
              {
                key: 'Loss-of-pay basis',
                value: slip.lop_basis
                  ? `${slip.lop_basis === 'calendar_days' ? 'Calendar days' : 'Working days'} · ${
                      slip.lop_policy === 'attendance' ? 'attendance-driven' : 'explicit absence only'
                    }`
                  : null,
              },
              { key: 'Generated', value: fmtDateTime(slip.created_at) },
            ]}
          />
        </>
      )}
    </Drawer>
  );
}

/**
 * One section of a payslip.
 *
 * `basis` and `rate` are shown because they are the answer to "where does
 * this number come from", a payslip line that says only "HRA 22,500" is
 * unverifiable, one that says "50% of 45,000" is not.
 */
function LineTable({ rows, negative = false }) {
  return (
    <MiniTable>
      {rows.map((line) => (
        <tr key={line.code}>
          <td style={{ width: '45%' }}>
            {line.name}
            {line.basis != null && num(line.rate) > 0 ? (
              <div style={{ fontSize: 11, color: 'var(--ac-faint)' }}>
                {fmtNumber(line.rate)}% of {fmtMoney(line.basis)}
              </div>
            ) : null}
          </td>
          <td className="ac-td--num" style={{ color: 'var(--ac-muted)' }}>
            {/* Shown only when loss of pay actually reduced the line, so a
                normal payslip is not cluttered with two equal columns. */}
            {num(line.full_amount) !== num(line.amount) ? `of ${fmtMoney(line.full_amount)}` : ''}
          </td>
          <td className="ac-td--num" style={{ width: 110 }}>
            <strong style={{ color: negative ? NEG_FG : 'var(--ac-ink)' }}>
              {negative && num(line.amount) > 0 ? '− ' : ''}
              {fmtMoney(line.amount)}
            </strong>
          </td>
        </tr>
      ))}
    </MiniTable>
  );
}

/* =====================================================================
   SALARIES, who can be paid, and what they earn
   ================================================================== */

function SalariesTab({ coverage, canAssign, onDone }) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [editing, setEditing] = useState(null);

  const rows = useMemo(() => coverage?.rows || [], [coverage]);
  const debouncedSearch = useDebounced(search, 250);

  const filtered = useMemo(
    () =>
      rows.filter(
        (row) =>
          matchesSearch(row, debouncedSearch, ['full_name', 'code', 'department', 'position']) &&
          (!stateFilter || row.state === stateFilter)
      ),
    [rows, debouncedSearch, stateFilter]
  );

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['admin'] });
    onDone?.();
  };

  const confirmImport = useMutation({
    mutationFn: (userId) => backendApi.payroll.confirmImportedSalary(userId),
    onSuccess: () => {
      toast.success('Salary confirmed');
      refresh();
    },
    onError: (err) => toast.error(err.message || 'Could not confirm this salary'),
  });

  const columns = useMemo(
    () => [
      {
        key: 'full_name',
        label: 'Employee',
        width: 220,
        render: (row) => <PersonCell name={row.full_name} sub={row.code || row.position} />,
      },
      { key: 'department', label: 'Department', width: 150, render: (row) => orDash(row.department) },
      {
        key: 'state',
        label: 'Status',
        width: 180,
        render: (row) => (
          <Chip tone={COVERAGE_TONE[row.state] || 'neutral'} dot>
            {COVERAGE_LABEL[row.state] || row.state}
          </Chip>
        ),
      },
      {
        key: 'annual_gross',
        label: 'Annual gross',
        width: 130,
        align: 'right',
        render: (row) => (row.annual_gross ? fmtMoney(row.annual_gross) : DASH),
      },
      {
        key: 'monthly',
        label: 'Monthly',
        width: 120,
        align: 'right',
        render: (row) => (row.annual_gross ? fmtMoney(num(row.annual_gross) / 12) : DASH),
        csv: (row) => (row.annual_gross ? num(row.annual_gross) / 12 : ''),
      },
      {
        key: 'structure_name',
        label: 'Structure',
        width: 190,
        render: (row) => orDash(row.structure_name),
      },
      {
        key: 'effective_from',
        label: 'Effective',
        width: 110,
        render: (row) => fmtDate(row.effective_from),
      },
      {
        key: 'actions',
        label: '',
        width: 170,
        sortable: false,
        render: (row) =>
          canAssign ? (
            <div style={{ display: 'flex', gap: 5, justifyContent: 'flex-end' }}>
              {row.state === 'needs_review' ? (
                <Button
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    confirmImport.mutate(row.id);
                  }}
                >
                  Confirm
                </Button>
              ) : null}
              <Button
                size="sm"
                variant="ghost"
                icon={Pencil}
                onClick={(e) => {
                  e.stopPropagation();
                  setEditing(row);
                }}
              >
                {row.state === 'missing' ? 'Set' : 'Revise'}
              </Button>
            </div>
          ) : null,
      },
    ],
    [canAssign, confirmImport]
  );

  return (
    <>
      {num(coverage?.counts?.needs_review) > 0 ? (
        <div style={{ marginBottom: 14 }}>
          <Notice tone="warning" icon={AlertTriangle} title="Imported salaries need confirming">
            This release moved salaries out of a single unlabelled column into an effective-dated record
            with a structure attached. The old column's unit was never stored, this database held a
            monthly figure for one organization and an annual one for another, so every imported row was
            read as a monthly gross and flagged. Check the annual figure, then confirm or revise it.
          </Notice>
        </div>
      ) : null}

      <DataTable
        columns={columns}
        rows={filtered}
        onRowClick={canAssign ? (row) => setEditing(row) : undefined}
        defaultSort={{ key: 'full_name', dir: 'asc' }}
        emptyIcon={BadgeIndianRupee}
        emptyTitle="No employees"
        emptyText="Add people to the directory first."
        toolbar={
          <Toolbar>
            <SearchInput value={search} onChange={setSearch} placeholder="Search employee…" width={210} />
            <FilterChip
              label="Status"
              value={stateFilter}
              options={Object.entries(COVERAGE_LABEL).map(([value, label]) => ({ value, label }))}
              onChange={setStateFilter}
            />
            <ToolbarSpacer />
            <span style={{ fontSize: 11.5, color: 'var(--ac-muted)' }}>
              {fmtNumber(num(coverage?.counts?.ready))} ready · {fmtNumber(filtered.length)} shown
            </span>
          </Toolbar>
        }
      />

      <SalaryModal
        employee={editing}
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          refresh();
        }}
      />
    </>
  );
}

/**
 * Setting or revising one person's salary.
 *
 * The effective date is required and prominent, because this does not
 * overwrite the previous figure, it closes it. That is what keeps a
 * payslip from six months ago explainable after a raise.
 */
function SalaryModal({ employee, open, onClose, onSaved }) {
  const [annualGross, setAnnualGross] = useState('');
  const [structureId, setStructureId] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(toISODate());
  const [monthlyTds, setMonthlyTds] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState(null);

  const { data: structures = [] } = useQuery({
    queryKey: ['admin', 'salary-structures'],
    queryFn: () => backendApi.payroll.structures(),
    enabled: open,
  });

  const { data: history = [] } = useQuery({
    queryKey: ['admin', 'salary-history', employee?.id],
    queryFn: () => backendApi.payroll.salaryHistory(employee.id),
    enabled: open && Boolean(employee?.id),
  });

  // Seeded from the current record so a revision starts from what is
  // already true, rather than from blank fields.
  useEffect(() => {
    if (!open || !employee) return;
    setAnnualGross(employee.annual_gross ? String(num(employee.annual_gross)) : '');
    setEffectiveFrom(toISODate());
    setMonthlyTds('');
    setNote('');
    setError(null);
  }, [open, employee]);

  useEffect(() => {
    if (!open) return;
    const current = history[0];
    const fallback = structures.find((s) => s.is_default);
    setStructureId(String(current?.structure_id || fallback?.id || ''));
  }, [open, history, structures]);

  const save = useMutation({
    mutationFn: () =>
      backendApi.payroll.assignSalary(employee.id, {
        annual_gross: Number(annualGross),
        structure_id: structureId ? Number(structureId) : null,
        effective_from: effectiveFrom,
        monthly_tds: monthlyTds === '' ? null : Number(monthlyTds),
        note: note || null,
      }),
    onSuccess: () => {
      toast.success(`Salary recorded for ${employee.full_name}`);
      onSaved();
    },
    onError: (err) => setError(err.message || 'Could not save this salary'),
  });

  const submit = (event) => {
    event.preventDefault();
    setError(null);
    if (!annualGross || Number(annualGross) <= 0) {
      setError('Enter an annual gross greater than zero');
      return;
    }
    save.mutate();
  };

  const monthly = annualGross ? num(annualGross) / 12 : 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={employee ? `Salary · ${employee.full_name}` : 'Salary'}
      sub="Recorded with an effective date. The previous record is closed, not replaced."
      footer={
        <>
          <Button onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} loading={save.isPending}>
            Save salary
          </Button>
        </>
      }
    >
      <form onSubmit={submit}>
        {error ? (
          <div style={{ marginBottom: 12 }}>
            <Notice tone="negative" icon={AlertTriangle}>
              {error}
            </Notice>
          </div>
        ) : null}

        <Grid cols={2}>
          <Field
            label="Annual gross"
            required
            hint={monthly ? `${fmtMoney(monthly)} per month` : 'The figure the structure must sum to'}
          >
            <Input
              type="number"
              min="0"
              step="1"
              value={annualGross}
              onChange={(e) => setAnnualGross(e.target.value)}
              autoFocus
            />
          </Field>
          <Field label="Effective from" required hint="Payroll for earlier periods is unaffected">
            <Input type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />
          </Field>
          <Field label="Salary structure" required>
            <Select
              value={structureId}
              onChange={(e) => setStructureId(e.target.value)}
              options={structures
                .filter((s) => s.is_active || String(s.id) === structureId)
                .map((s) => ({
                  value: String(s.id),
                  label: s.is_default ? `${s.name} (default)` : s.name,
                }))}
              placeholder="Select a structure"
            />
          </Field>
          <Field
            label="Monthly income tax"
            hint="Entered, not computed, see the note below"
          >
            <Input
              type="number"
              min="0"
              step="1"
              value={monthlyTds}
              onChange={(e) => setMonthlyTds(e.target.value)}
              placeholder="None"
            />
          </Field>
        </Grid>

        <Field label="Note" hint="Why this changed. Recorded in the audit trail.">
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Annual review" />
        </Field>

        <div style={{ marginTop: 12 }}>
          <Notice tone="info" icon={Info} title="Income tax is not calculated">
            Computing TDS correctly needs the employee's regime election, investment declarations with
            proofs, perquisite valuation, any previous-employer income and a full-year projection revised
            every month. Approximating it would put a wrong figure on a statutory document and
            under-deduct against a real liability, so this field is the amount your payroll adviser
            works out, and it appears on the payslip exactly as entered. Leave it blank for none.
          </Notice>
        </div>

        {history.length ? (
          <>
            <div className="ac-eyebrow" style={{ margin: '20px 0 7px' }}>
              History
            </div>
            <MiniTable>
              {history.map((record) => (
                <tr key={record.id}>
                  <td>{fmtDate(record.effective_from)}</td>
                  <td style={{ color: 'var(--ac-muted)' }}>
                    {record.effective_to ? `to ${fmtDate(record.effective_to)}` : 'current'}
                  </td>
                  <td>{record.structure_name}</td>
                  <td className="ac-td--num">{fmtMoney(record.annual_gross)}</td>
                  <td className="ac-td--num" style={{ color: 'var(--ac-faint)', fontSize: 11 }}>
                    {record.source === 'migrated_from_users_salary' ? 'imported' : record.source}
                  </td>
                </tr>
              ))}
            </MiniTable>
          </>
        ) : null}
      </form>
    </Modal>
  );
}

/* =====================================================================
   SETUP, the rules payroll computes against
   ================================================================== */

function SetupTab({ canConfigure }) {
  const queryClient = useQueryClient();

  const { data: settings, isLoading } = useQuery({
    queryKey: ['admin', 'payroll-statutory'],
    queryFn: () => backendApi.payroll.statutory(),
  });

  const { data: structures = [] } = useQuery({
    queryKey: ['admin', 'salary-structures'],
    queryFn: () => backendApi.payroll.structures(),
  });

  const [form, setForm] = useState(null);
  const [openStructureId, setOpenStructureId] = useState(null);

  useEffect(() => {
    if (settings) setForm(settings);
  }, [settings]);

  const save = useMutation({
    mutationFn: (payload) => backendApi.payroll.updateStatutory(payload),
    onSuccess: () => {
      toast.success('Statutory settings saved');
      queryClient.invalidateQueries({ queryKey: ['admin'] });
    },
    onError: (err) => toast.error(err.message || 'Could not save these settings'),
  });

  const set = (key) => (eventOrValue) => {
    const value =
      eventOrValue && eventOrValue.target !== undefined
        ? eventOrValue.target.type === 'checkbox'
          ? eventOrValue.target.checked
          : eventOrValue.target.value
        : eventOrValue;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  if (isLoading || !form) return <LoadingBlock label="Loading payroll settings…" />;

  return (
    <>
      <Panel style={{ marginBottom: 14 }}>
        <PanelHead
          title="Loss of pay"
          sub="The two decisions that most change what people are paid"
          actions={
            canConfigure ? (
              <Button
                variant="primary"
                onClick={() =>
                  save.mutate({
                    lop_policy: form.lop_policy,
                    lop_basis: form.lop_basis,
                  })
                }
                loading={save.isPending}
              >
                Save
              </Button>
            ) : null
          }
        />
        <PanelBody>
          <Grid cols={2}>
            <Field
              label="What counts as loss of pay"
              hint="Changing this to attendance-driven will reduce pay for any unmarked day"
            >
              <Select
                value={form.lop_policy}
                onChange={set('lop_policy')}
                disabled={!canConfigure}
                options={[
                  { value: 'explicit', label: 'Explicit absence and unpaid leave only' },
                  { value: 'attendance', label: 'Any payable day with no attendance' },
                ]}
              />
            </Field>
            <Field label="Per-day rate is the monthly gross divided by">
              <Select
                value={form.lop_basis}
                onChange={set('lop_basis')}
                disabled={!canConfigure}
                options={[
                  { value: 'working_days', label: "Working days in the month (per the employee's shift)" },
                  { value: 'calendar_days', label: 'Every calendar day in the month' },
                ]}
              />
            </Field>
          </Grid>

          {form.lop_policy === 'attendance' ? (
            <div style={{ marginTop: 12 }}>
              <Notice tone="warning" icon={AlertTriangle} title="Attendance now drives pay">
                A payable day with no punch, no HR attendance record and no approved leave will be treated
                as unpaid. Only choose this if attendance is actually being captured for everyone, otherwise a device outage becomes a pay cut.
              </Notice>
            </div>
          ) : (
            <div style={{ marginTop: 12 }}>
              <Notice tone="info" icon={Info}>
                Pay is reduced only for unpaid leave and for days somebody explicitly marked absent. A day
                nobody recorded anything about is paid.
              </Notice>
            </div>
          )}
        </PanelBody>
      </Panel>

      <Panel style={{ marginBottom: 14 }}>
        <PanelHead
          title="Statutory contributions"
          sub="Rates are stored, not compiled in, because they change by statute"
          actions={
            canConfigure ? (
              <Button
                variant="primary"
                onClick={() =>
                  save.mutate({
                    pf_enabled: form.pf_enabled,
                    pf_employee_rate: form.pf_employee_rate,
                    pf_employer_rate: form.pf_employer_rate,
                    pf_wage_ceiling: form.pf_wage_ceiling,
                    pf_cap_at_ceiling: form.pf_cap_at_ceiling,
                    esi_enabled: form.esi_enabled,
                    esi_employee_rate: form.esi_employee_rate,
                    esi_employer_rate: form.esi_employer_rate,
                    esi_gross_limit: form.esi_gross_limit,
                    pt_enabled: form.pt_enabled,
                    pt_state: form.pt_state,
                    tds_enabled: form.tds_enabled,
                    rounding: form.rounding,
                  })
                }
                loading={save.isPending}
              >
                Save
              </Button>
            ) : null
          }
        />
        <PanelBody>
          <div className="ac-eyebrow" style={{ marginBottom: 8 }}>
            Provident fund
          </div>
          <Grid cols={4}>
            <Field label="Enabled">
              <div style={{ paddingTop: 7 }}>
                <Checkbox
                  checked={Boolean(form.pf_enabled)}
                  onChange={set('pf_enabled')}
                  disabled={!canConfigure}
                  label="Deduct PF"
                />
              </div>
            </Field>
            <Field label="Employee rate" hint="% of PF wages">
              <Input
                type="number"
                step="0.01"
                value={form.pf_employee_rate}
                onChange={set('pf_employee_rate')}
                disabled={!canConfigure}
              />
            </Field>
            <Field label="Employer rate" hint="% of PF wages">
              <Input
                type="number"
                step="0.01"
                value={form.pf_employer_rate}
                onChange={set('pf_employer_rate')}
                disabled={!canConfigure}
              />
            </Field>
            <Field label="Monthly wage ceiling" hint="25,000 from 17 Sep 2026">
              <Input
                type="number"
                step="1"
                value={form.pf_wage_ceiling}
                onChange={set('pf_wage_ceiling')}
                disabled={!canConfigure}
              />
            </Field>
          </Grid>
          <div style={{ marginTop: 4 }}>
            <Checkbox
              checked={Boolean(form.pf_cap_at_ceiling)}
              onChange={set('pf_cap_at_ceiling')}
              disabled={!canConfigure}
              label="Cap PF wages at the ceiling (clear this to contribute on full basic)"
            />
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--ac-muted)', marginTop: 6 }}>
            PF wages are the components marked as counting toward PF on the salary structure, normally
            Basic, and Dearness Allowance where it exists. Not the gross.
          </div>

          <div className="ac-eyebrow" style={{ margin: '22px 0 8px' }}>
            Employees' State Insurance
          </div>
          <Grid cols={4}>
            <Field label="Enabled">
              <div style={{ paddingTop: 7 }}>
                <Checkbox
                  checked={Boolean(form.esi_enabled)}
                  onChange={set('esi_enabled')}
                  disabled={!canConfigure}
                  label="Deduct ESI"
                />
              </div>
            </Field>
            <Field label="Employee rate" hint="% of gross">
              <Input
                type="number"
                step="0.01"
                value={form.esi_employee_rate}
                onChange={set('esi_employee_rate')}
                disabled={!canConfigure}
              />
            </Field>
            <Field label="Employer rate" hint="% of gross">
              <Input
                type="number"
                step="0.01"
                value={form.esi_employer_rate}
                onChange={set('esi_employer_rate')}
                disabled={!canConfigure}
              />
            </Field>
            <Field label="Monthly gross limit" hint="Applies at or below this">
              <Input
                type="number"
                step="1"
                value={form.esi_gross_limit}
                onChange={set('esi_gross_limit')}
                disabled={!canConfigure}
              />
            </Field>
          </Grid>
          <div style={{ fontSize: 11.5, color: 'var(--ac-muted)', marginTop: 6 }}>
            Eligibility is judged on the full monthly gross, so a month with heavy unpaid leave does not
            briefly pull a higher earner into ESI.
          </div>

          <div className="ac-eyebrow" style={{ margin: '22px 0 8px' }}>
            Professional tax
          </div>
          <Grid cols={3}>
            <Field label="Enabled">
              <div style={{ paddingTop: 7 }}>
                <Checkbox
                  checked={Boolean(form.pt_enabled)}
                  onChange={set('pt_enabled')}
                  disabled={!canConfigure}
                  label="Deduct professional tax"
                />
              </div>
            </Field>
            <Field label="State">
              <Input value={form.pt_state || ''} onChange={set('pt_state')} disabled={!canConfigure} />
            </Field>
            <Field label="Rounding">
              <Select
                value={form.rounding}
                onChange={set('rounding')}
                disabled={!canConfigure}
                options={[
                  { value: 'nearest_rupee', label: 'Nearest rupee' },
                  { value: 'paise', label: 'Two decimal places' },
                ]}
              />
            </Field>
          </Grid>

          {Array.isArray(form.pt_slabs) && form.pt_slabs.length ? (
            <MiniTable
              head={['Monthly gross', 'Tax per month']}
              aligns={['left', 'right']}
              style={{ marginTop: 10, maxWidth: 420 }}
            >
              {form.pt_slabs.map((slab) => (
                <tr key={`${slab.min}-${slab.max ?? 'top'}`}>
                  <td>
                    {fmtMoney(slab.min)} {slab.max == null ? 'and above' : `, ${fmtMoney(slab.max)}`}
                  </td>
                  <td className="ac-td--num">{fmtMoney(slab.amount)}</td>
                </tr>
              ))}
            </MiniTable>
          ) : null}

          {!form.pt_enabled ? (
            <div style={{ marginTop: 12 }}>
              <Notice tone="info" icon={Info} title="Professional tax is off">
                It is levied by individual states at their own slabs, so there is no correct nationwide
                default and guessing one would deduct a tax you may not owe. The slabs above are
                {form.pt_state ? ` ${form.pt_state}'s` : ' a'} schedule, seeded as a worked example, check them against your state before switching this on.
              </Notice>
            </div>
          ) : null}

          <div className="ac-eyebrow" style={{ margin: '22px 0 8px' }}>
            Income tax
          </div>
          <Checkbox
            checked={Boolean(form.tds_enabled)}
            onChange={set('tds_enabled')}
            disabled={!canConfigure}
            label="Show income tax on payslips, using the per-employee monthly figure"
          />
          <div style={{ marginTop: 8 }}>
            <Notice tone="info" icon={Info}>
              TDS is not calculated here. The amount withheld is the one recorded against each employee
              under Salaries, which is what your payroll adviser computes. Enabling this makes it appear
              on the payslip as a deduction; leaving it off means no income tax line.
            </Notice>
          </div>
        </PanelBody>
      </Panel>

      <Panel>
        <PanelHead title="Salary structures" sub="How a gross is split into components" />
        <PanelBody>
          {structures.length ? (
            <MiniTable
              head={['Structure', 'Components', 'Assigned', '', '']}
              aligns={['left', 'right', 'right', 'left', 'right']}
            >
              {structures.map((structure) => (
                <tr key={structure.id}>
                  <td>
                    <strong>{structure.name}</strong>
                    {structure.description ? (
                      <div style={{ fontSize: 11.5, color: 'var(--ac-muted)' }}>
                        {structure.description}
                      </div>
                    ) : null}
                  </td>
                  <td className="ac-td--num">{fmtNumber(structure.component_count)}</td>
                  <td className="ac-td--num">{fmtNumber(structure.assigned_count)}</td>
                  <td>
                    {structure.is_default ? <Chip tone="info">default</Chip> : null}
                    {!structure.is_active ? <Chip tone="neutral">inactive</Chip> : null}
                  </td>
                  <td className="ac-td--num">
                    <Button
                      size="sm"
                      variant="ghost"
                      icon={FileSpreadsheet}
                      onClick={() => setOpenStructureId(structure.id)}
                    >
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </MiniTable>
          ) : null}
          {!structures.length ? (
            <EmptyState
              icon={FileSpreadsheet}
              title="No salary structures"
              text="A tenant is provisioned with a standard structure; if this is empty, something went wrong during setup."
            />
          ) : null}
        </PanelBody>
      </Panel>

      <StructureDrawer structureId={openStructureId} onClose={() => setOpenStructureId(null)} />
    </>
  );
}

/** A structure's components, and how each one resolves. */
function StructureDrawer({ structureId, onClose }) {
  const { data: structure, isLoading } = useQuery({
    queryKey: ['admin', 'salary-structure', structureId],
    queryFn: () => backendApi.payroll.structure(structureId),
    enabled: Boolean(structureId),
  });

  const describe = (component) => {
    switch (component.calc) {
      case 'fixed':
        return `Fixed ${fmtMoney(component.amount)}`;
      case 'percent_of_gross':
        return `${fmtNumber(component.rate)}% of the monthly gross`;
      case 'percent_of_basic':
        return `${fmtNumber(component.rate)}% of basic`;
      case 'balance':
        return 'The remainder, so earnings sum to the gross exactly';
      case 'statutory':
        return 'Computed from the statutory settings';
      default:
        return component.calc;
    }
  };

  return (
    <Drawer
      open={Boolean(structureId)}
      onClose={onClose}
      title={structure?.name || 'Salary structure'}
      sub={structure?.description}
      badge={structure?.is_default ? <Chip tone="info">default</Chip> : null}
      wide
      footer={<Button onClick={onClose}>Close</Button>}
    >
      {isLoading ? (
        <LoadingBlock label="Loading the structure…" />
      ) : !structure ? (
        <EmptyState icon={Info} title="Structure not found" />
      ) : (
        <>
          {structure.problems?.length ? (
            <div style={{ marginBottom: 14 }}>
              <Notice
                tone="negative"
                icon={AlertTriangle}
                title="This structure cannot produce a payslip"
              >
                <div style={{ display: 'grid', gap: 3, marginTop: 4 }}>
                  {structure.problems.map((problem) => (
                    <div key={problem} style={{ fontSize: 12 }}>
                      {problem}
                    </div>
                  ))}
                </div>
                A payroll run refuses the employees on it rather than computing a wrong figure.
              </Notice>
            </div>
          ) : null}

          {['earning', 'deduction', 'employer_contribution'].map((kind) => {
            const rows = structure.components.filter((c) => c.kind === kind);
            if (!rows.length) return null;
            const heading =
              kind === 'earning'
                ? 'Earnings'
                : kind === 'deduction'
                  ? 'Deductions'
                  : 'Employer contributions';
            return (
              <div key={kind} style={{ marginBottom: 18 }}>
                <div className="ac-eyebrow" style={{ marginBottom: 7 }}>
                  {heading}
                </div>
                <MiniTable>
                  {rows.map((component) => (
                    <tr key={component.component_id}>
                      <td style={{ width: '38%' }}>
                        <strong>{component.name}</strong>
                        <div style={{ fontSize: 11, color: 'var(--ac-faint)' }}>{component.code}</div>
                      </td>
                      <td style={{ color: 'var(--ac-body)' }}>{describe(component)}</td>
                      <td style={{ width: 150 }}>
                        <div
                          style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', flexWrap: 'wrap' }}
                        >
                          {component.counts_for_pf ? <Chip tone="info">PF wages</Chip> : null}
                          {component.prorate_on_lop === false ? <Chip>not pro-rated</Chip> : null}
                          {component.override_rate != null || component.override_amount != null ? (
                            <Chip tone="warning">overridden</Chip>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </MiniTable>
              </div>
            );
          })}

          <Notice tone="info" icon={Info} title="How a gross becomes a payslip">
            Components resolve in order: flat and gross-relative amounts first, then anything quoted as a
            percentage of basic, then the balancing component takes up the remainder. Loss of pay is
            applied to earnings, and statutory contributions are computed on the wages actually paid, not on the full-month entitlement.
          </Notice>
        </>
      )}
    </Drawer>
  );
}
